import React, { useEffect, useState } from "react";
import MagicTable from "../common/MagicTable";
import TableHeader from "../common/MagicPageHeader";
import Header from "../common/MagicTableHeader";
import ImageComponent from "../mui/Image";
import DownloadToXlsxFile from "../../helper/DownloadToXlsxFile";
import { useFetchAttendanceReportsQuery } from "../../store/apis/reportsApis";

interface TabCategory {
  id: number;
  name: string;
  code: string;
}

interface AttendanceTableProps {
  selectedTab: TabCategory | null;
  magicPageHeaderBtn: {
    text: string;
    variant: string;
    startIcon: React.ReactNode;
    onClick: () => void;
    sx: Record<string, string>;
  }[];
  magicTableHeaderBtn: any[];
  params:any;
  isInvalidSearch: boolean;
  onPaginationChange: (pageNo: number) => void;
  onSearch: (searchTerm: string) => void;
}

const AttendanceTable: React.FC<AttendanceTableProps> = ({
  selectedTab,
  magicTableHeaderBtn,
  params,
  isInvalidSearch,
  onPaginationChange,
  onSearch,
}) => {
  const [downloadParams, setDownloadParams] = useState<any>(null);
  const [downloadTrigger, setDownloadTrigger] = useState(false);
  const columns = [
    { field: "centre", headerName: "Centre", flex: 1, minWidth: 150 },
    { field: "project", headerName: "Project", flex: 1, minWidth: 150 },
    { field: "batch", headerName: "Batch", flex: 1, minWidth: 150 },
    {
      field: "batchStartDate",
      headerName: "Batch Start Date",
      flex: 1,
      minWidth: 150,
      type: "date",
    },
    {
      field: "batchEndDate",
      headerName: "Batch End Date",
      flex: 1,
      minWidth: 150,
      type: "date",
    },
    { field: "name", headerName: "Name", flex: 1, minWidth: 150 },
    { field: "gender", headerName: "Gender", flex: 1, minWidth: 100 },
    {
      field: "attendancePresent",
      headerName: "Attendance Present",
      flex: 1,
      minWidth: 100,
      sortable: true,
      renderCell: (params: any) => params.row.attendancePresent ,
    },
    {
      field: "attendanceSessions",
      headerName: "Attendance Session Happened",
      flex: 1,
      minWidth: 100,
      sortable: true,
      renderCell: (params: any) => params.row.attendanceSessions.toString() || "-",
    },
    {
      field: "attendancePercentage",
      headerName: "Attendance %",
      flex: 1,
      minWidth: 100,
      sortable: true,
      renderCell: (params: any) => params.row.attendancePercentage || "-",
    },
  ];

  const {
    data: fetchedReports,isLoading
  } = useFetchAttendanceReportsQuery(params);
  const { data: downloadData,isLoading: downloadLoading } = useFetchAttendanceReportsQuery(downloadParams, {
    skip: !downloadParams,
  });
  const handleDownload = async () => {
    try {
      const downloadPayload = {
        ...params,
        pageNo: "1",
        pageSize: fetchedReports?.totalReports || "10",
      };
      
      setDownloadParams(downloadPayload);
      setDownloadTrigger(true); 
    } catch (error) {
      console.error("Error during download:", error);
    }
  };

  useEffect(()=>{ 
    if (downloadData && downloadTrigger) {
    const sheetData = [
      [
        "Centre",
        "Project",
        "Batch",
        "Batch Start Date",
        "Batch End Date",
        "Name",
        "Gender",
        "Attendance Present",
        "Attendance Session Happened",
        "Attendance %",
      ],
      ...downloadData?.reports.map((report: any) => [
        report.centre || "-",
        report.project || "-",
        report.batch || "-",
        report.batchStartDate || "-",
        report.batchEndDate || "-",
        report.name || "-",
        report.gender || "-",
        report.attendancePresent || "0",
        report.attendanceSessions || "0",
        report.attendancePercentage || "0",
      ]),
    ];

    DownloadToXlsxFile({
      fileNamePrifix: "attendance-report",
      sheetData,
    }); setDownloadTrigger(false);  
  }
  }, [downloadData, downloadTrigger]);  

  const magicPageHeaderBtn = [
    {
      text: "Download",
      variant: "outlined",
      startIcon: (
        <ImageComponent src="/assets/images/login/exportIcon.svg" alt="ExportIcon" />
      ),
      onClick: handleDownload,
      loading: downloadLoading,
      sx: {
        background: "white",
        border: "1px solid #D0D5DD",
        borderRadius: "8px",
        color: "black",
      },
    },
  ];
  const totalPages = Math.max(1, Math.ceil((fetchedReports?.totalReports ?? 0) / 10));

  return (
    <>
      <TableHeader
        title={`${selectedTab?.name || "Attendance"} Report`}
        subTitle="Here you can view the report with all the details."
        buttons={magicPageHeaderBtn}
        chipLabel={`${fetchedReports?.totalReports} entries`}
      />
      <Header
        buttons={magicTableHeaderBtn}
        onSearchfieldChange={(value) => onSearch(String(value))}
        isInvalidSearch={isInvalidSearch}
      />
      <MagicTable
        columns={columns}
        rows={fetchedReports?.reports}
        loading={isLoading}
        handlePageChange={(event, pageNo) => onPaginationChange(pageNo)}
        count={totalPages}
        currentPage={parseInt(params?.pageNo) || 1}
        rowCellsSx={{ paddingLeft: "16px", textAlign: "start" }}
        headerSx={{
          paddingLeft: "16px",
          textAlign: "start",
          fontWeight: 600,
          color: "#242424",
          padding: "13px 16px",
        }}
        tableSx={{ minHeight: "300px", width: "100%" }}
        sx={{
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#DADADA",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 550,
            fontSize: "14px",
          },
          "& .MuiDataGrid-row": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },}}
      />
    </>
  );
};

export default AttendanceTable;
